import { useState, useEffect, useRef } from 'react';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  GlobeAltIcon,
  CpuChipIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '../assets/icons/craftech-logo.png';
import LanguageSwitcher from './LanguageSwitcher';

const navigation = [
  { nameKey: 'nav.home', href: '/' },
  { nameKey: 'nav.aboutUs', href: '/about-us' },
  { nameKey: 'nav.contact', href: '/Contact' },
];

const services = [
  {
    nameKey: 'services.websiteDevelopment.name',
    descriptionKey: 'services.websiteDevelopment.description',
    href: '/services/website-development',
    icon: GlobeAltIcon,
    gradient: 'from-blue-500 to-cyan-400',
  },
  {
    nameKey: 'services.webAppDevelopment.name',
    descriptionKey: 'services.webAppDevelopment.description',
    href: '/services/web-application-development',
    icon: CpuChipIcon,
    gradient: 'from-purple-500 to-pink-400',
  },
  {
    nameKey: 'services.seoPerformance.name',
    descriptionKey: 'services.seoPerformance.description',
    href: '/services/seo-performance',
    icon: RocketLaunchIcon,
    gradient: 'from-primary to-secondary',
  },
];

export default function Navbar() {
  const { t } = useTranslation('common');
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const servicesRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setServicesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown when route changes
  useEffect(() => {
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  const isActive = (href) => location.pathname === href;
  const isServicesActive = services.some(service => location.pathname === service.href);

  return (
    <Disclosure as="nav" className={`fixed w-full top-0 left-0 z-50 transition-all duration-300 ${
      scrolled
        ? 'bg-gray-900/95 backdrop-blur-lg shadow-lg shadow-black/10'
        : 'bg-transparent'
    }`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 justify-between items-center">
          {/* Mobile menu button */}
          <div className="mr-2 -ml-2 flex items-center md:hidden">
            <DisclosureButton className="relative inline-flex items-center justify-center rounded-lg p-2 text-gray-400 hover:text-white hover:bg-white/10 transition-colors focus:outline-none">
              {({ open }) => (
                <>
                  <span className="absolute -inset-0.5" />
                  <span className="sr-only">Open main menu</span>
                  <div className="relative w-6 h-6">
                    <Bars3Icon
                      className={`absolute inset-0 h-6 w-6 transition-all duration-300 ${open ? 'opacity-0 rotate-90' : 'opacity-100 rotate-0'}`}
                      aria-hidden="true"
                    />
                    <XMarkIcon
                      className={`absolute inset-0 h-6 w-6 transition-all duration-300 ${open ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-90'}`}
                      aria-hidden="true"
                    />
                  </div>
                </>
              )}
            </DisclosureButton>
          </div>

          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img
              alt="Craftech"
              src={logo}
              className="h-10 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-1 ml-10">
            {/* Home Link */}
            <Link
              to="/"
              className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg ${
                isActive('/')
                  ? 'text-primary'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              {t('nav.home')}
              <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-300 ${
                isActive('/') ? 'w-6' : 'w-0'
              }`}></span>
            </Link>

            {/* Services Dropdown */}
            <div ref={servicesRef} className="relative">
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                onMouseEnter={() => setServicesOpen(true)}
                className={`relative flex items-center gap-1 px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg ${
                  isServicesActive
                    ? 'text-primary'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {t('nav.services')}
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`} />
                <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-300 ${
                  isServicesActive ? 'w-6' : 'w-0'
                }`}></span>
              </button>

              {/* Dropdown Panel */}
              <div
                onMouseLeave={() => setServicesOpen(false)}
                className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-80 transition-all duration-300 ${
                  servicesOpen
                    ? 'opacity-100 visible translate-y-0'
                    : 'opacity-0 invisible -translate-y-2'
                }`}
              >
                {/* Arrow */}
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-gray-800 rotate-45 border-l border-t border-white/10"></div>

                <div className="relative bg-gray-800/95 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl shadow-black/20 overflow-hidden">
                  {/* Decorative gradient line */}
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent"></div>

                  <div className="p-2">
                    {services.map((service) => (
                      <Link
                        key={service.nameKey}
                        to={service.href}
                        className={`group flex items-center gap-4 p-3 rounded-xl transition-all duration-300 ${
                          isActive(service.href)
                            ? 'bg-white/10'
                            : 'hover:bg-white/5'
                        }`}
                      >
                        <div className={`flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br ${service.gradient} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                          <service.icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className={`text-sm font-semibold transition-colors duration-300 ${
                            isActive(service.href) ? 'text-primary' : 'text-white group-hover:text-primary'
                          }`}>
                            {t(service.nameKey)}
                          </div>
                          <div className="text-xs text-gray-400">
                            {t(service.descriptionKey)}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* About Link */}
            <Link
              to="/about-us"
              className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg ${
                isActive('/about-us')
                  ? 'text-primary'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              {t('nav.aboutUs')}
              <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-300 ${
                isActive('/about-us') ? 'w-6' : 'w-0'
              }`}></span>
            </Link>

            {/* Pricing Link */}
            <Link
              to="/pricing"
              className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg ${
                isActive('/pricing')
                  ? 'text-primary'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              {t('nav.pricing')}
              <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-300 ${
                isActive('/pricing') ? 'w-6' : 'w-0'
              }`}></span>
            </Link>

            {/* Contact Link */}
            <Link
              to="/Contact"
              className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg ${
                isActive('/Contact')
                  ? 'text-primary'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              {t('nav.contact')}
              <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-300 ${
                isActive('/Contact') ? 'w-6' : 'w-0'
              }`}></span>
            </Link>
          </div>

          {/* CTA Button & Language Switcher */}
          <div className="hidden md:flex items-center gap-2">
            <LanguageSwitcher />
            <Link
              to="/Contact"
              className="inline-flex items-center px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-primary to-secondary rounded-full hover:shadow-lg hover:shadow-primary/25 transition-all duration-300 hover:scale-105"
            >
              {t('nav.getInTouch')}
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      <DisclosurePanel className="md:hidden">
        {({ close }) => (
          <>
            {/* Backdrop overlay - closes menu when clicked */}
            <div
              className="fixed inset-0 top-20 bg-black/50 backdrop-blur-sm z-[-1]"
              onClick={() => close()}
            />
            <div className="bg-gray-900/95 backdrop-blur-lg border-t border-white/10 px-4 pt-4 pb-6 space-y-2">
          {/* Home */}
          <DisclosureButton
            as={Link}
            to="/"
            className={`block px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 ${
              isActive('/')
                ? 'bg-primary/10 text-primary'
                : 'text-gray-300 hover:bg-white/5 hover:text-white'
            }`}
          >
            {t('nav.home')}
          </DisclosureButton>

          {/* Services Accordion */}
          <div>
            <button
              onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
              className={`flex items-center justify-between w-full px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 ${
                isServicesActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-gray-300 hover:bg-white/5 hover:text-white'
              }`}
            >
              {t('nav.services')}
              <ChevronDownIcon className={`w-5 h-5 transition-transform duration-300 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
            </button>

            <div className={`overflow-hidden transition-all duration-300 ${mobileServicesOpen ? 'max-h-96 mt-2' : 'max-h-0'}`}>
              <div className="pl-4 space-y-1">
                {services.map((service) => (
                  <DisclosureButton
                    key={service.nameKey}
                    as={Link}
                    to={service.href}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                      isActive(service.href)
                        ? 'bg-white/10 text-primary'
                        : 'text-gray-400 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${service.gradient} flex items-center justify-center`}>
                      <service.icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm font-medium">{t(service.nameKey)}</span>
                  </DisclosureButton>
                ))}
              </div>
            </div>
          </div>

          {/* About */}
          <DisclosureButton
            as={Link}
            to="/about-us"
            className={`block px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 ${
              isActive('/about-us')
                ? 'bg-primary/10 text-primary'
                : 'text-gray-300 hover:bg-white/5 hover:text-white'
            }`}
          >
            {t('nav.aboutUs')}
          </DisclosureButton>

          {/* Pricing */}
          <DisclosureButton
            as={Link}
            to="/pricing"
            className={`block px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 ${
              isActive('/pricing')
                ? 'bg-primary/10 text-primary'
                : 'text-gray-300 hover:bg-white/5 hover:text-white'
            }`}
          >
            {t('nav.pricing')}
          </DisclosureButton>

          {/* Contact */}
          <DisclosureButton
            as={Link}
            to="/Contact"
            className={`block px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 ${
              isActive('/Contact')
                ? 'bg-primary/10 text-primary'
                : 'text-gray-300 hover:bg-white/5 hover:text-white'
            }`}
          >
            {t('nav.contact')}
          </DisclosureButton>

          {/* Language Switcher - Mobile */}
          <div className="border-t border-white/10 mt-4 pt-4">
            <LanguageSwitcher mobile />
          </div>

          <Link
            to="/Contact"
            className="block w-full mt-4 px-4 py-3 text-center text-base font-semibold text-white bg-gradient-to-r from-primary to-secondary rounded-xl"
          >
            {t('nav.getInTouch')}
          </Link>
            </div>
          </>
        )}
      </DisclosurePanel>
    </Disclosure>
  );
}
